import { useRef, useEffect, forwardRef } from 'react'
import styled from 'styled-components'
import CloseButton from '../generic/CloseButton'
import useOutsideClick from '../../hooks/useOutsideClick'

//Import the Animation
import { SlideUp } from '../../styles/animations'

/**
 * The modals that are open, innermost last.
 *
 * Escape has to be handled on the document, not on the dialog element: a
 * `onKeyDown` there only fires when focus is already inside the modal, so
 * clicking the backdrop and pressing Escape did nothing — which is most of the
 * time. Listening on the document instead means every modal answers, so they
 * take turns: only the last one registered acts, and it stops the event so the
 * one behind it stays open.
 */
const openModals = []

function mergeRefs(...refs) {
    return (el) => {
        refs.forEach((ref) => {
            if (!ref) return;
            if (typeof ref === 'function') {
                ref(el);
            } else {
                ref.current = el;
            }
        });
    };
}

export const ModalBackdrop = styled.div`
    --backdrop-zindex: 1050;
    --backdrop-bg: ${({ theme }) => theme.modal.backdrop};
    --backdrop-opacity: ${({ opacity }) => opacity ? opacity : 0};
    position: fixed;
    top: 0;
    left: 0;
    z-index: var(--backdrop-zindex);
    width: 100vw;
    height: 100vh;
    background-color: var(--backdrop-bg);
    opacity: var(--backdrop-opacity);
    visibility: ${({ $show }) => $show === true ? 'visible' : 'hidden'};

    @media (max-width: 575.98px) {
        ${({ $mode, theme }) => $mode === 'fullscreen' ? `background-color: ${theme.modal.background}; opacity: 1;` : `
            top: env(safe-area-inset-top);
            height: calc(100svh - env(safe-area-inset-top));
        `}
    }


    //Animation:
    &.animate:not(.fullscreen) {
        transition: opacity 0.15s linear;
        opacity: var(--backdrop-opacity);        
    }

    &.animate.fullscreen {
        @media (min-width: 576px) {
           transition: opacity 0.15s linear;
           opacity: var(--backdrop-opacity);        
        }
    }


`

const ModalArea = styled.div`
    --modal-zindex: 1055;
    --modal-width: 500px;
    --modal-padding: 1rem;
    --modal-margin: 0.5rem;
    --modal-color: ${({ theme }) => theme.font.color};
    --modal-bg: ${({ theme }) => theme.modal.background} ;
    --modal-border-color: ${({ theme }) => theme.modal.border};
    --modal-border-width: 1px;
    --modal-border-radius: 0.5rem;
    --modal-box-shadow: 0 0.125rem 0.25rem ${({ theme }) => theme.modal.shadow};
    --modal-inner-border-radius: calc(0.5rem - 1px);
    --modal-header-padding-x: 1rem;
    --modal-header-padding-y: 1rem;
    --modal-header-padding: 1rem 1rem;
    --modal-header-border-color: ${({ theme }) => theme.modal.divider};
    --modal-header-border-width: 1px;
    --modal-title-line-height: 1.5;
    --modal-footer-gap: 0.5rem;
    --modal-footer-bg: ;
    --modal-footer-border-color: ${({ theme }) => theme.modal.divider};
    --modal-footer-border-width: 1px;
    position: fixed;
    top: 0;
    left: 0;
    z-index: var(--modal-zindex);
    visibility: ${({ $show }) => $show === true ? 'visible' : 'hidden'};
    width: 100%;
    height: 100%;
    overflow-x: hidden;
    overflow-y: auto;
    outline: 0;
    
    @media (min-width: 576px)
     {
    --modal-margin: 1.75rem;
    --modal-box-shadow: 0 0.5rem 1rem ${({ theme }) => theme.modal.shadow_m};
    }
`

const ModalDialog = styled.div`

    //Centered
    display: flex;
    align-items: center;
    min-height: calc(100% - var(--modal-margin) * 2);

    position: relative;
    width: auto;
    margin: var(--modal-margin);
    pointer-events: none;


    &.animate{
        transition: transform 0.15s ease-out;
        transform: translateY(-50px);
    }

    @media (min-width: 576px)
     {
    max-width: var(--modal-width);
    margin-right: auto;
    margin-left: auto;
    }

    &.fullscreen{
        @media (max-width: 575.98px) {
        width: 100vw;
        max-width: none;
        min-height: 100vh; /* Minimum height is the full viewport height */
        margin: 0;
        transform: translateY(0px);
        animation: ${SlideUp} 0.25s ease-in-out forwards;
            }
    }

`

const ModalContent = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    color: ${({ theme, $color }) => $color ? $color : theme.font.color}; 
    pointer-events: auto;
    background-color: ${({ theme, $bg }) => $bg ? $bg : theme.modal.background}; 
    background-clip: padding-box;
    border: var(--modal-border-width) solid var(--modal-border-color);
    border-radius: var(--modal-border-radius);
    outline: 0;
    min-height: ${(props) => props.$minHeight ? props.$minHeight : '15rem'};
    margin: ${(props) => props.$margin ? props.$margin : '0'};

    @media (min-width: 575.98px) {
        transform: translateY(50px);
    }



    &.fullscreen{

    @media (max-width: 575.98px) {
    min-height: 100vh;
    border: 0;
    border-radius: 0;
    margin: 0;
}
}

`

const Modal = forwardRef(function Modal(
    {
        zIndex = 1000,
        onClose = () => { },
        setShow = () => { },
        show = true,
        children,
        mode = '',
        handleKeyDown,
        minHeight,
        // styled-component props (keep the $-prefix – that’s how SC passes them)
        opacity = 0.5,
        $padding = '1rem',
        $color,
        $bg,
        $margin
    },
    modalRef // forwarded ref
) {
    /* ------------------------------------------------------------------ */
    /* helpers                                                             */
    /* ------------------------------------------------------------------ */
    const close = () => {
        setShow(false);
        onClose();
    };

    /* close when clicking outside the dialog */
    const contentRef = useRef(null);
    useOutsideClick(contentRef, close, 'modal');

    /* close on Escape — topmost modal only */
    const closeRef = useRef(close);
    closeRef.current = close;
    useEffect(() => {
        if (!show) return;
        const token = {};
        openModals.push(token);

        const onKeyDown = (e) => {
            if (e.key !== 'Escape') return;
            if (openModals[openModals.length - 1] !== token) return;
            e.stopPropagation();
            closeRef.current();
        };

        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('keydown', onKeyDown);
            const i = openModals.indexOf(token);
            if (i !== -1) openModals.splice(i, 1);
        };
    }, [show]);

    /* ------------------------------------------------------------------ */
    /* render                                                              */
    /* ------------------------------------------------------------------ */
    return (
        <div style={{ zIndex }}>
            {/* backdrop ------------------------------------------------------- */}
            <ModalBackdrop
                name="Modal-Backdrop"
                $show={show}
                opacity={opacity}
                $mode={mode}
                className={show ? 'animate' : ''}
            />

            {/* modal shell ---------------------------------------------------- */}
            <ModalArea name="ModalArea" $show={show}>
                <ModalDialog
                    name="Modal-Dialog"
                    onKeyDown={handleKeyDown}
                    className={show ? `animate ${mode}` : ''}
                >
                    <ModalContent
                        ref={mergeRefs(contentRef, modalRef)}
                        name="Modal-Content"
                        className={show ? `animate ${mode}` : ''}
                        $minHeight={minHeight}
                        $padding={$padding}
                        $color={$color}
                        $bg={$bg}
                        $margin={$margin}
                    >
                        {children}
                    </ModalContent>
                </ModalDialog>
            </ModalArea>
        </div>
    );
});



const ModalTitle = styled.div`
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: space-between;
    padding: var(--modal-header-padding);
    border-bottom: var(--modal-header-border-width) solid;
    border-bottom-color: ${({ theme, $color }) => $color ? $color : theme.modal.divider};
    border-top-left-radius: var(--modal-inner-border-radius);
    border-top-right-radius: var(--modal-inner-border-radius);
    
    h4 {
        margin-right: 2.25rem;
    }
`

const ModalHeader = ({ children, $color, onClose, testId }) => {
    return (
        <>
            <ModalTitle $color={$color}>{children}</ModalTitle>
            <CloseButton $color={$color} handleClick={onClose} $size='1.5rem' testId={testId && `${testId}.dismiss`}></CloseButton>
        </>

    )
}


const ModalBody = styled.div`
    position: relative;
    flex: 1 1 auto; 
    padding: ${({ $padding }) => $padding ? $padding : '1rem'};
`


const ModalFooter = styled.div`
    display: flex;
    flex-shrink: 0;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-end;
    padding: calc(var(--modal-padding) - var(--modal-footer-gap) * .5);
    background-color: var(--modal-footer-bg);
    border-top: var(--modal-footer-border-width) solid;
    border-top-color: ${({ theme, $color }) => $color ? $color : theme.modal.divider};
    border-bottom-right-radius: var(--modal-inner-border-radius);
    border-bottom-left-radius: var(--modal-inner-border-radius);
`



Modal.Dialog = ModalDialog
Modal.Content = ModalContent
Modal.Header = ModalHeader
Modal.Body = ModalBody
Modal.Footer = ModalFooter

export default Modal